import { useState } from "react";
import type { ChangeEvent, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import _ from "lodash";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import { changePasswordSchema, emailSchema, verificationSchema } from "../../utils/validations";
import { sendResetCode, confirmReset } from "./Service";
import Snackbar from "../../components/Snackbar";
import AuthPageBackground from "../../components/AuthPageBackground";
import { useGlobalStyles } from "../../components/globalStyles";
import { commonStrings, forgotPasswordStrings } from "../../components/StringBundle";
import logoPng from "../../assets/images/logo_pdf.png";
import type {
  SnackState,
  ConfirmForgotPasswordPayload,
  ForgotPasswordResponse,
  ConfirmForgotPasswordResponse,
} from "./types";

// Validates the new password field, returning an error message or "" if valid.
function getPasswordError(password: string): string {
  if (!password) return forgotPasswordStrings.passwordRequired;
  if (password.length < 8) return forgotPasswordStrings.passwordError;
  return "";
}

// Validates the confirm-password field against the new password.
function getConfirmPasswordError(password: string, confirmPassword: string): string {
  if (!confirmPassword) return forgotPasswordStrings.confirmPasswordRequired;
  if (password !== confirmPassword) return forgotPasswordStrings.passwordMismatch;
  return "";
}

// Forgot-password screen: first sends a reset code, then lets the user set a new password.
export default function ForgetPassword() {
  const classes = useGlobalStyles() as Record<string, string>;
  const navigate = useNavigate();

  const [changePass, setChangePass] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSnack, setSnack] = useState<SnackState>({ open: false, message: "", severity: "" });

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");

  const [code, setCode] = useState<string>("");
  const [codeError, setCodeError] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  // Hides the current snackbar message.
  const closeSnack = () => setSnack({ open: false, message: "", severity: "" });

  // Updates the email field as the user types.
  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError("");
  };

  // Updates the verification code field, keeping digits only (max 6).
  const onChangeCode = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
    setCodeError("");
  };

  // Updates the new password field and re-checks it against confirm-password.
  const onChangePassword = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, "");
    setPassword(value);
    setPasswordError("");
    setConfirmPasswordError(confirmPassword && confirmPassword !== value ? forgotPasswordStrings.passwordMismatch : "");
  };

  // Updates the confirm-password field and checks it matches the new password.
  const onChangeConfirmPassword = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, "");
    setConfirmPassword(value);
    setConfirmPasswordError(value && value !== password ? forgotPasswordStrings.passwordMismatch : "");
  };

  // Validates the email, then asks the backend to send a reset code.
  const sendVerificationCode = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const value = emailSchema.validate({ email }, { errors: { escapeHtml: true } });
    if (value?.error?.details.length) {
      setEmailError(value.error.details[0].message);
      return;
    }

    setLoading(true);
    try {
      const response: ForgotPasswordResponse = await sendResetCode(email);
      if (!_.isEmpty(response)) {
        setChangePass(true);
        setSnack({ open: true, message: forgotPasswordStrings.resetCodeSent, severity: "success" });
      } else {
        throw forgotPasswordStrings.resetError;
      }
    } catch (err: unknown) {
      // caught errors have no known shape — narrow with typeof before use, don't assume
      setSnack({
        open: true,
        message: typeof err === "string" ? err : forgotPasswordStrings.resetError,
        severity: commonStrings.error,
      });
    } finally {
      setLoading(false);
    }
  };

  // Validates the code + new password, then submits the password reset.
  const onSubmitForgotPassword = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const nextCodeError = !code ? forgotPasswordStrings.codeRequired : "";
    const nextPasswordError = getPasswordError(password);
    const nextConfirmPasswordError = getConfirmPasswordError(password, confirmPassword);

    setCodeError(nextCodeError);
    setPasswordError(nextPasswordError);
    setConfirmPasswordError(nextConfirmPasswordError);
    if (nextCodeError || nextPasswordError || nextConfirmPasswordError) return;

    setLoading(true);
    let redirecting = false;
    try {
      const data: ConfirmForgotPasswordPayload = {
        emailid: email,
        confirmationcode: code,
        password,
        last_password_modified_date: new Date(),
      };
      const value = verificationSchema.validate({ code, password });
      if (value?.error?.details.length) {
        if (value.error.details[0].path.includes("password")) {
          setPasswordError(forgotPasswordStrings.passwordError);
          setCodeError("");
          setTimeout(() => setPasswordError(""), 5000);
        } else {
          setCodeError(forgotPasswordStrings.codeError);
        }
      } else {
        const response: ConfirmForgotPasswordResponse = await confirmReset(data);
        if (_.isEmpty(response)) {
          redirecting = true;
          setSnack({ open: true, message: forgotPasswordStrings.resetSuccess, severity: "success" });
          setTimeout(() => navigate("/"), 5000);
        }
      }
    } catch (err: unknown) {
      // caught errors have no known shape — narrow with a type guard before use, don't assume
      const serverMessage =
        axios.isAxiosError(err) && typeof err.response?.data === "string"
          ? err.response.data.split(":")[1]?.trim()
          : undefined;
      setSnack({ open: true, message: serverMessage || forgotPasswordStrings.resetError, severity: "error" });
      navigate("/forget");
    } finally {
      // Keep the submit button disabled through the redirect delay above, not just the request itself.
      if (!redirecting) setLoading(false);
    }
  };

  const passwordMeetsRequirements = !changePasswordSchema.validate({ password }).error;

  // Extra styles for the reset-password step, which has more fields.
  const logoClass = `${classes.loginLogo} ${classes.loginLogoForgot}${changePass ? ` ${classes.loginLogoForgotStep2}` : ""}`;
  const headingClass = `${classes.authHeading} ${classes.authHeadingForgot}${changePass ? ` ${classes.authHeadingForgotStep2}` : ""}`;
  const subtitleClass = `${classes.loginSubtitle} ${classes.loginSubtitleForgot}${changePass ? ` ${classes.loginSubtitleForgotStep2}` : ""}`;
  const fieldClass = `${classes.loginField} ${classes.loginFieldForgot}${changePass ? ` ${classes.loginFieldForgotStep2}` : ""}`;
  const labelClass = `${classes.loginLabel} ${classes.loginLabelForgot}${changePass ? ` ${classes.loginLabelForgotStep2}` : ""}`;
  const inputWrapClass = `${classes.loginInputWrap} ${classes.loginInputWrapForgot}${changePass ? ` ${classes.loginInputWrapForgotStep2}` : ""}`;
  const submitClass = `${classes.loginSubmit} ${classes.loginSubmitForgot}${changePass ? ` ${classes.loginSubmitForgotStep2}` : ""}`;
  const footerClass = `${classes.loginFooter} ${classes.loginFooterForgot}${changePass ? ` ${classes.loginFooterForgotStep2}` : ""}`;

  return (
    <div className={classes.loginPage}>
      <AuthPageBackground />
      <div className={classes.loginCard}>
        <div className={classes.loginImagePanel} aria-hidden="true" />

        <div className={classes.loginFormPanel}>
          {showSnack.open && (
            <Snackbar severity={showSnack.severity} message={showSnack.message} handleClose={closeSnack} />
          )}

          <img src={logoPng} alt={commonStrings.logoAlt} className={logoClass} />
          <Typography variant="h1" className={headingClass}>{changePass ? forgotPasswordStrings.resetPassword : forgotPasswordStrings.title}</Typography>
          <Typography className={subtitleClass}>{changePass ? forgotPasswordStrings.subtitle2 : forgotPasswordStrings.subtitle1}</Typography>

          {!changePass ? (
            <form className={classes.loginForm} noValidate>
              <div className={fieldClass}>
                <label htmlFor="forgot-email" className={labelClass}>{forgotPasswordStrings.emailAddress}</label>
                <div className={`${inputWrapClass}${emailError ? ` ${classes.loginInputError}` : ""}`}>
                  <MailOutlineIcon className={classes.loginInputIcon} />
                  <InputBase
                    id="forgot-email"
                    type="text"
                    autoComplete="username"
                    autoFocus
                    fullWidth
                    value={email}
                    placeholder={forgotPasswordStrings.emailPlaceholder}
                    onChange={onChangeEmail}
                    onFocus={closeSnack}
                  />
                </div>
                {!!emailError && <span className={classes.loginErrorText}>{emailError}</span>}
              </div>

              <Button type="submit" className={submitClass} disabled={loading} onClick={sendVerificationCode} disableRipple>
                {loading ? commonStrings.loading : forgotPasswordStrings.sendResetLink}
              </Button>
            </form>
          ) : (
            <form className={classes.loginForm} noValidate>
              <div className={fieldClass}>
                <label htmlFor="forgot-code" className={labelClass}>{forgotPasswordStrings.verificationCode}</label>
                <div className={`${inputWrapClass}${codeError ? ` ${classes.loginInputError}` : ""}`}>
                  <VpnKeyOutlinedIcon className={classes.loginInputIcon} />
                  <InputBase
                    id="forgot-code"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    fullWidth
                    value={code}
                    placeholder={forgotPasswordStrings.verificationCodePlaceholder}
                    onChange={onChangeCode}
                    onFocus={closeSnack}
                  />
                </div>
                {!!codeError && <span className={classes.loginErrorText}>{codeError}</span>}
              </div>

              <div className={fieldClass}>
                <label htmlFor="forgot-password" className={labelClass}>{forgotPasswordStrings.newPassword}</label>
                <div className={`${inputWrapClass}${passwordError ? ` ${classes.loginInputError}` : ""}`}>
                  <LockOutlinedIcon className={classes.loginInputIcon} />
                  <InputBase
                    id="forgot-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    fullWidth
                    value={password}
                    placeholder={forgotPasswordStrings.newPasswordPlaceholder}
                    onChange={onChangePassword}
                    onFocus={closeSnack}
                  />
                  <IconButton
                    type="button"
                    className={classes.loginEyeBtn}
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((show) => !show)}
                    onMouseDown={(e) => e.preventDefault()}
                    disableRipple
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </div>
                {passwordError ? (
                  <span className={classes.loginErrorText}>{passwordError}</span>
                ) : (
                  !passwordMeetsRequirements && (
                    <span className={classes.loginHintText}>{forgotPasswordStrings.passwordHint}</span>
                  )
                )}
              </div>

              <div className={fieldClass}>
                <label htmlFor="forgot-confirm-password" className={labelClass}>{forgotPasswordStrings.confirmPassword}</label>
                <div className={`${inputWrapClass}${confirmPasswordError ? ` ${classes.loginInputError}` : ""}`}>
                  <LockOutlinedIcon className={classes.loginInputIcon} />
                  <InputBase
                    id="forgot-confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="new-password"
                    fullWidth
                    value={confirmPassword}
                    placeholder={forgotPasswordStrings.confirmPasswordPlaceholder}
                    onChange={onChangeConfirmPassword}
                    onFocus={closeSnack}
                  />
                  <IconButton
                    type="button"
                    className={classes.loginEyeBtn}
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword((show) => !show)}
                    onMouseDown={(e) => e.preventDefault()}
                    disableRipple
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </div>
                {!!confirmPasswordError && <span className={classes.loginErrorText}>{confirmPasswordError}</span>}
              </div>

              <Button
                type="submit"
                className={submitClass}
                disabled={loading || !code || !password || !confirmPassword}
                onClick={onSubmitForgotPassword}
                disableRipple
              >
                {loading ? commonStrings.loading : forgotPasswordStrings.changePassword}
              </Button>
            </form>
          )}

          <Typography className={footerClass}>
            {forgotPasswordStrings.rememberedIt}{" "}
            <Link href="/login" className={classes.loginForgot}>{forgotPasswordStrings.backToLogin}</Link>
          </Typography>
        </div>
      </div>
    </div>
  );
}
